import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainScreenHeader from '../../common/Header/MainScreenHeader';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import ErrorMessage from '../../common/ErrorMessage/ErrorMessage';
import RoleGuard from '../../common/RoleGuard';
import { UserRoles } from '../../../constants/UserRoles';
import { TaskItemRestDataProvider } from '../../../data/TaskItemRestDataProvider';
import { TaskItem, TaskPriority } from '../../../models/TaskItem';
import { ToolbarFilterConfig } from '../../common/Toolbar/Toolbar';
import TaskDialog from './TaskDialog';
import './TaskManagement.scss';
import '../../../styles/GenericSiteStyles.scss';

type StatusFilter = '' | 'open' | 'completed';
type PriorityFilter = '' | '0' | '1' | '2';

const TaskManagement: React.FC = () => {
    const { t } = useTranslation(['common', 'screens']);
    const [tasks, setTasks] = useState<TaskItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState<TaskItem | null>(null);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
    const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('');

    const provider = useMemo(() => new TaskItemRestDataProvider(), []);

    const loadTasks = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setTasks(await provider.getAll());
        } catch (e: any) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [provider]);

    useEffect(() => { loadTasks(); }, [loadTasks]);

    const displayedTasks = useMemo(() => {
        let filtered = tasks;
        if (statusFilter === 'open') filtered = filtered.filter(t => !t.isCompleted);
        else if (statusFilter === 'completed') filtered = filtered.filter(t => t.isCompleted);
        if (priorityFilter !== '') filtered = filtered.filter(t => t.priority === parseInt(priorityFilter, 10));
        return filtered;
    }, [tasks, statusFilter, priorityFilter]);

    const priorityBadgeClass = (priority: TaskPriority) => {
        if (priority === TaskPriority.High) return 'badge bg-danger';
        if (priority === TaskPriority.Medium) return 'badge bg-warning text-dark';
        return 'badge bg-secondary';
    };

    const formatDate = (dateStr: string | null): string => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const columns: DetailedListColumn<TaskItem>[] = useMemo(() => [
        {
            key: 'title',
            header: t('screens:tasks.columns.title'),
            dataField: 'title',
            type: 'string',
        },
        {
            key: 'priority',
            header: t('screens:tasks.columns.priority'),
            dataField: 'priority',
            width: 110,
            fixedWidth: true,
            render: (item) => (
                <span className={priorityBadgeClass(item.priority)}>
                    {t(`screens:tasks.priority.${item.priority}`)}
                </span>
            ),
        },
        {
            key: 'dueDate',
            header: t('screens:tasks.columns.dueDate'),
            dataField: 'dueDate',
            width: 130,
            fixedWidth: true,
            render: (item) => {
                if (!item.dueDate) return <span className="text-muted">—</span>;
                const date = new Date(item.dueDate);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const isOverdue = !item.isCompleted && date < today;
                return (
                    <span className={isOverdue ? 'task-management__overdue' : ''}>
                        {formatDate(item.dueDate)}
                    </span>
                );
            },
        },
        {
            key: 'isCompleted',
            header: t('screens:tasks.columns.status'),
            dataField: 'isCompleted',
            width: 120,
            fixedWidth: true,
            render: (item) => item.isCompleted
                ? <span className="badge bg-success">{t('screens:tasks.status.completed')}</span>
                : <span className="badge bg-primary">{t('screens:tasks.status.open')}</span>,
        },
        {
            key: 'createdAt',
            header: t('screens:tasks.columns.createdAt'),
            dataField: 'createdAt',
            width: 130,
            fixedWidth: true,
            render: (item) => <span>{formatDate(item.createdAt)}</span>,
        },
    ], [t]);

    const toolbarFilters: ToolbarFilterConfig[] = useMemo(() => [
        {
            key: 'status-filter',
            type: 'select',
            label: t('screens:tasks.filter.status'),
            value: statusFilter,
            onChange: (v) => setStatusFilter(v as StatusFilter),
            options: [
                { label: t('screens:tasks.status.open'), value: 'open' },
                { label: t('screens:tasks.status.completed'), value: 'completed' },
            ],
            allLabel: t('screens:tasks.filter.all'),
            width: 140,
        },
        {
            key: 'priority-filter',
            type: 'select',
            label: t('screens:tasks.filter.priority'),
            value: priorityFilter,
            onChange: (v) => setPriorityFilter(v as PriorityFilter),
            options: [
                { label: t('screens:tasks.priority.0'), value: '0' },
                { label: t('screens:tasks.priority.1'), value: '1' },
                { label: t('screens:tasks.priority.2'), value: '2' },
            ],
            allLabel: t('screens:tasks.filter.all'),
            width: 130,
        },
    ], [t, statusFilter, priorityFilter]);

    const openEdit = (task: TaskItem) => {
        setSelectedTask(task);
        setDialogOpen(true);
    };

    const openAdd = () => {
        setSelectedTask(null);
        setDialogOpen(true);
    };

    const closeDialog = () => setDialogOpen(false);

    const handleSave = async (task: TaskItem): Promise<TaskItem> => {
        try {
            if (!task.id) {
                const saved = { ...task, id: crypto.randomUUID() };
                await provider.add(saved);
                await loadTasks();
                return saved;
            } else {
                await provider.update(task.id, task);
                await loadTasks();
                return task;
            }
        } catch (e: any) {
            setError(e.message);
            throw e;
        }
    };

    const handleDelete = async (task: TaskItem) => {
        try {
            await provider.delete(task.id);
            closeDialog();
            await loadTasks();
        } catch (e: any) {
            setError(e.message);
        }
    };

    const handleComplete = async (task: TaskItem) => {
        try {
            await provider.complete(task.id);
            await loadTasks();
            setSelectedTask(prev => prev ? { ...prev, isCompleted: true, completedAt: new Date().toISOString() } : prev);
        } catch (e: any) {
            setError(e.message);
        }
    };

    const handleReopen = async (task: TaskItem) => {
        try {
            await provider.reopen(task.id);
            await loadTasks();
            setSelectedTask(prev => prev ? { ...prev, isCompleted: false, completedAt: null } : prev);
        } catch (e: any) {
            setError(e.message);
        }
    };

    return (
        <RoleGuard roles={`${UserRoles.Admin},${UserRoles.Manager},${UserRoles.User}`} redirectOnDeny>
            <div className="task-management-page">
                <MainScreenHeader
                    title={t('screens:tasks.title')}
                    subtitle={t('screens:tasks.subtitle')}
                />
                {error && <ErrorMessage message={error} />}
                <DetailedListComponent
                    items={displayedTasks}
                    columns={columns}
                    rowKey={task => task.id}
                    editRole={`${UserRoles.Admin},${UserRoles.Manager},${UserRoles.User}`}
                    addRole={`${UserRoles.Admin},${UserRoles.Manager},${UserRoles.User}`}
                    onEditRequest={openEdit}
                    onAddRequest={openAdd}
                    disableDelete
                    toolbarFilters={toolbarFilters}
                />
                {loading && (
                    <div className="task-management__loading">{t('common:loading')}</div>
                )}
                <TaskDialog
                    open={dialogOpen}
                    task={selectedTask}
                    onSave={handleSave}
                    onDelete={handleDelete}
                    onComplete={handleComplete}
                    onReopen={handleReopen}
                    onClose={closeDialog}
                />
            </div>
        </RoleGuard>
    );
};

export default TaskManagement;

